/**
 * Claves de los campos de capacidad / precio por rango (listingFields + formulario).
 */

export const getCapacityOptionFieldKey = opt => {
  if (!opt) return '';
  const raw = opt.option != null && opt.option !== '' ? opt.option : opt.key;
  if (raw == null || raw === '') return '';
  return String(raw);
};

/** Primera opción de capacidad (p. ej. `1-5`) cuando el host no mueve el slider. */
export const getDefaultCapacityOption = (capacityOptions = []) => {
  if (Array.isArray(capacityOptions) && capacityOptions.length > 0) {
    const key = getCapacityOptionFieldKey(capacityOptions[0]);
    if (key) return key;
  }
  return '1-5';
};

export const resolveSavedCapacity = publicData => {
  const { capacity, capacityRanges, capacityRange } = publicData || {};
  const saved =
    capacityRanges ||
    (Array.isArray(capacity) && capacity.length > 0 ? capacity[capacity.length - 1] : capacity) ||
    capacityRange ||
    '';
  return saved != null ? String(saved).trim() : '';
};

/**
 * Índice del máximo de capacidad guardado respecto a la lista de opciones de la consola.
 * Acepta option, key, value y variantes en string (como en el paso Capacidad).
 */
export const findCapacityIndexInOptions = (savedRaw, capacityOptions) => {
  if (
    savedRaw == null ||
    savedRaw === '' ||
    !Array.isArray(capacityOptions) ||
    capacityOptions.length === 0
  ) {
    return -1;
  }
  const saved = Array.isArray(savedRaw) ? savedRaw[savedRaw.length - 1] : savedRaw;
  const s = String(saved).trim();

  let idx = capacityOptions.findIndex(opt => {
    const o = opt.option;
    const k = opt.key;
    const v = opt.value;
    if (o != null && (o === saved || String(o) === s)) return true;
    if (k != null && (k === saved || String(k) === s)) return true;
    if (v != null && (v === saved || String(v) === s)) return true;
    if (String(getCapacityOptionFieldKey(opt)) === s) return true;
    return false;
  });
  if (idx >= 0) return idx;

  const norm = x =>
    String(x ?? '')
      .replace(/\s+/g, '')
      .toLowerCase();
  const ns = norm(s);
  idx = capacityOptions.findIndex(
    opt => norm(opt.option) === ns || norm(opt.key) === ns || norm(opt.value) === ns
  );
  return idx;
};

/**
 * Límites numéricos de un rango de huéspedes Cocopool (clave option/key del paso capacidad/precios).
 * @returns {{ min: number, max: number } | null} max puede ser Infinity (p. ej. +51).
 */
export const parseGuestTierBoundsFromCapacityKey = raw => {
  if (raw == null || raw === '') return null;
  const key = String(raw)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[\u2013\u2014]/g, '-'); // ASCII hyphen for en-dash / em-dash labels
  if (
    key === '51_above' ||
    key === '+51' ||
    key === '51+' ||
    key.includes('51_above') ||
    /^(?:\+?)51(?:\+)?$/.test(key)
  ) {
    return { min: 51, max: Number.POSITIVE_INFINITY };
  }
  const m = /^(\d+)-(\d+)$/.exec(key);
  if (!m) return null;
  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return { min: Math.min(a, b), max: Math.max(a, b) };
};

export const parseGuestTierBoundsFromCapOption = opt => {
  if (!opt) return null;
  const raw =
    opt.option != null && opt.option !== ''
      ? opt.option
      : opt.key != null && opt.key !== ''
      ? opt.key
      : opt.value;
  return parseGuestTierBoundsFromCapacityKey(raw ?? '');
};

const normalizeCapacityKey = raw =>
  String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[–—]/g, '-')
    .replace(/_/g, '-');

/**
 * Fila de `priceRange` que aplica a un tramo de grupo pedido (p. ej. el bucket del
 * buscador `1-5`), resuelta por intervalos numéricos y NO por igualdad de claves.
 *
 * Los tramos de un anuncio los define su anfitrión (o operaciones desde Console) y
 * no tienen por qué coincidir con los buckets globales del buscador: un anuncio
 * puede tener `1-20` y el buscador preguntar por `1-5`. Comparar claves en ese caso
 * no encuentra nada, y el precio acaba resolviéndose al tramo equivocado.
 *
 * El grupo se representa por el máximo del tramo pedido (el peor caso dentro del
 * bucket); si el tramo no tiene techo (`51_above`), por su mínimo.
 *
 * @param {Array} priceRange filas `{ quantityRange, amount, currency }` del anuncio
 * @param {string} guestRangeKey tramo pedido (bucket del buscador o clave propia del anuncio)
 * @returns {object|null} la fila aplicable, o null si el anuncio no llega a ese grupo
 */
export const resolvePriceRangeRowForGuestRange = (priceRange, guestRangeKey) => {
  const rows = Array.isArray(priceRange)
    ? priceRange.filter(r => r && r.quantityRange != null && r.quantityRange !== '')
    : [];
  if (rows.length === 0 || guestRangeKey == null || String(guestRangeKey).trim() === '') {
    return null;
  }

  // Claves idénticas (el caso normal, y la única salida para etiquetas no numéricas).
  const wanted = normalizeCapacityKey(guestRangeKey);
  const exact = rows.find(r => normalizeCapacityKey(r.quantityRange) === wanted);
  if (exact) {
    return exact;
  }

  const requested = parseGuestTierBoundsFromCapacityKey(guestRangeKey);
  if (!requested) {
    return null;
  }
  const target = Number.isFinite(requested.max) ? requested.max : requested.min;

  const measured = rows
    .map(row => ({ row, bounds: parseGuestTierBoundsFromCapacityKey(row.quantityRange) }))
    .filter(entry => entry.bounds);
  if (measured.length === 0) {
    return null;
  }

  const covering = measured.find(({ bounds }) => target >= bounds.min && target <= bounds.max);
  if (covering) {
    return covering.row;
  }

  // Grupo por debajo del primer tramo del anuncio: se aplica su tramo de entrada.
  const lowest = measured.reduce((a, b) => (b.bounds.min < a.bounds.min ? b : a));
  if (target < lowest.bounds.min) {
    return lowest.row;
  }

  // Grupo por encima de la capacidad del anuncio: no hay tramo aplicable.
  return null;
};

/** Tramo +51 (clave `51_above` o rango almacenado como `51-999`). */
export const isGuestTier51PlusKey = raw => {
  if (raw == null || raw === '') return false;
  const key = String(raw)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[\u2013\u2014]/g, '-');
  if (
    key === '51_above' ||
    key === '+51' ||
    key === '51+' ||
    key.includes('51_above') ||
    /^(?:\+?)51(?:\+)?$/.test(key)
  ) {
    return true;
  }
  const m = /^(\d+)-(\d+)$/.exec(key);
  if (!m) return false;
  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  return Number.isFinite(a) && Number.isFinite(b) && a >= 51 && b >= 999;
};

/**
 * Si el usuario eligió hasta N huéspedes, solo tiene sentido marcar por defecto tramos cuyo máximo ≤ N.
 * Si no podemos inferir cotas desde las claves, se conserva la heurística por índice en la lista consola.
 */
export const shouldDefaultSelectPriceTierForCapacity = (
  tierOption,
  selectedCapacityRaw,
  capacityOptions
) => {
  const selectedIdx =
    capacityOptions?.length >= 1
      ? findCapacityIndexInOptions(selectedCapacityRaw, capacityOptions)
      : -1;
  const tierIdx =
    capacityOptions?.length >= 1
      ? capacityOptions.findIndex(
          opt => getCapacityOptionFieldKey(opt) === getCapacityOptionFieldKey(tierOption)
        )
      : -1;

  const tierBounds = parseGuestTierBoundsFromCapOption(tierOption);
  const selectedOpt = selectedIdx >= 0 && capacityOptions ? capacityOptions[selectedIdx] : null;
  const selectedBounds = parseGuestTierBoundsFromCapOption(selectedOpt);

  if (tierBounds && selectedBounds && Number.isFinite(selectedBounds.max)) {
    return tierBounds.max <= selectedBounds.max;
  }
  // Capacidad "sin techo": marcar todos los tramos conocidos como candidatos por defecto.
  if (tierBounds && selectedBounds?.max === Number.POSITIVE_INFINITY) {
    return true;
  }
  // No pudimos obtener ambas cotas desde las etiquetas opción/console: mismo criterio que antes (orden).
  if (tierIdx >= 0 && selectedIdx >= 0) {
    return tierIdx <= selectedIdx;
  }
  return false;
};
